<!-- src/components/ScrollShadowContainer.vue -->
<template>
    <div
        ref="container"
        class="relative grow overflow-y-auto"
        @scroll="onScroll"
    >
        <!-- Top shadow -->
        <div
            class="pointer-events-none sticky top-0 left-0 right-0 h-4 bg-gradient-to-b from-black/15 to-transparent z-20"
            :style="{ opacity: topOpacity }"
        />

        <!-- wrap your slot so we can observe its size -->
        <div ref="content" class="pb-6">
            <slot />
        </div>

        <!-- Bottom shadow -->
        <div
            class="pointer-events-none sticky bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-black/15 to-transparent z-20"
            :style="{ opacity: bottomOpacity }"
        />
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { eventBus } from '@/Composables';

const FADE_DISTANCE = 64;
const container    = ref(null);
const content      = ref(null);
const scrollTop    = ref(0);
const scrollHeight = ref(0);
const clientHeight = ref(0);

function updateMetrics() {
    const el = container.value;
    if (!el) {
        scrollTop.value = 0;
        scrollHeight.value = 0;
        clientHeight.value = 0;
        return;
    }
    scrollTop.value    = el.scrollTop;
    scrollHeight.value = el.scrollHeight;
    clientHeight.value = el.clientHeight;
}

function onScroll() {
    updateMetrics();
    eventBus.emit('pins:dialog-scrolled'); // Will close these dropdowns when scrolled
}

let ro;
onMounted(() => {
    updateMetrics();
    // watch the slot content for any size change:
    ro = new ResizeObserver(updateMetrics);
    if (content.value) ro.observe(content.value);
});

onBeforeUnmount(() => {
    if (ro) ro.disconnect();
});

const isScrollable = computed(() => scrollHeight.value > clientHeight.value);
const fade         = v => Math.min(Math.max(v / FADE_DISTANCE, 0), 1);

const topOpacity = computed(() => isScrollable.value ? fade(scrollTop.value) : 0);

const bottomOpacity = computed(() => {
    if (!isScrollable.value) return 0;
    const remaining = scrollHeight.value - (scrollTop.value + clientHeight.value);
    return fade(remaining);
});
</script>
